/**
 * IXVerify Auth Context
 *
 * Provides IXVerify-specific authentication and user management.
 * Works alongside the main AuthContext from the Status Board app.
 * Includes CFR 21 Part 11 compliance features like session timeout.
 */

import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../../services/supabase';
import api, { getCurrentSiteId } from '../../services/api';
import type { IXVerifyUser, IXVerifyRole } from '../types';
import { useSessionTimeout, getAuthMethodFromSession, type AuthMethod } from '../hooks/useSessionTimeout';
import { logLoginSuccess, logLogout } from '../lib/auditLog';

interface IXVerifyAuthContextType {
  user: IXVerifyUser | null;
  session: Session | null;
  supabaseUser: SupabaseUser | null;
  loading: boolean;
  authMethod: AuthMethod;
  sessionTimeout: {
    isWarningVisible: boolean;
    minutesRemaining: number;
    dismissWarning: () => void;
  };
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'azure') => Promise<void>;
  signOut: () => Promise<void>;
}

// Storage key for invitation token during OAuth flow
const INVITATION_TOKEN_KEY = 'ixverify_pending_invitation_token';

const IXVerifyAuthContext = createContext<IXVerifyAuthContextType | undefined>(undefined);

export function IXVerifyAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<IXVerifyUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState<AuthMethod>(null);
  const [warningDismissed, setWarningDismissed] = useState(false);

  // Use refs to track state across renders without causing re-runs of useEffect
  const fetchingProfileRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);
  const userRef = useRef<IXVerifyUser | null>(null);

  // Keep userRef in sync with user state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Handle session logout (called at midnight for password users)
  const handleSessionLogout = useCallback(async () => {
    console.log('[IXVerifyAuthContext] Session timeout - logging out user');
    try {
      // Log session expiration before clearing state
      if (userRef.current) {
        await logLogout(userRef.current.id, null, 'session_expired');
      }
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setSupabaseUser(null);
      setAuthMethod(null);
    } catch (error) {
      console.error('[IXVerifyAuthContext] Error during session timeout logout:', error);
    }
  }, []);

  // Session timeout hook - only active for password users
  const { isWarningVisible, minutesRemaining } = useSessionTimeout({
    authMethod,
    onLogout: handleSessionLogout,
    warningMinutes: 10,
  });

  // Update auth method when session changes
  useEffect(() => {
    if (session) {
      const method = getAuthMethodFromSession(session);
      setAuthMethod(method);
      setWarningDismissed(false); // Reset dismissal on new session
    } else {
      setAuthMethod(null);
    }
  }, [session]);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!mounted) {
          return;
        }

        setSession(session);
        setSupabaseUser(session?.user ?? null);

        if (session?.user) {
          await fetchUserProfileGuarded(session.user.id);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('[IXVerifyAuthContext] Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    const fetchUserProfileGuarded = async (userId: string) => {
      // Skip if we're already fetching
      if (fetchingProfileRef.current) {
        setLoading(false);
        return;
      }
      // Skip if we already have this user loaded
      if (currentUserIdRef.current === userId && userRef.current) {
        setLoading(false);
        return;
      }

      fetchingProfileRef.current = true;
      currentUserIdRef.current = userId;
      await fetchUserProfile(userId);
      fetchingProfileRef.current = false;
    };

    initAuth();

    // Listen for auth changes (after initial load)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) {
          return;
        }

        // Only update session/supabaseUser if actually changed
        setSession(session);
        setSupabaseUser(session?.user ?? null);

        if (event === 'SIGNED_IN' && session?.user) {
          // Only fetch if this is a different user or we don't have a user yet
          if (currentUserIdRef.current !== session.user.id || !userRef.current) {
            await fetchUserProfileGuarded(session.user.id);
          } else {
            setLoading(false);
          }
        } else if (event === 'SIGNED_OUT') {
          currentUserIdRef.current = null;
          setUser(null);
          setLoading(false);
        } else if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // Empty dependency array - only run once on mount

  const fetchUserProfile = async (userId: string) => {
    try {
      // Get site_id from localStorage (set by main app's site selector)
      const siteId = getCurrentSiteId();

      // Fetch user profile via the platform API (data decoupled from supabase in
      // WP-3.4). site_id is managed via user_site_access, not on users.
      let data: any = null;
      let error: unknown = null;
      try {
        const profileResp = await api.get('/api/users/me');
        data = profileResp.data?.user;
      } catch (err) {
        error = err;
      }

      if (error || !data) {
        // User profile might not exist yet - create a minimal user from Supabase auth
        console.warn('[IXVerifyAuthContext] Could not fetch user profile:', error);
        // Get the Supabase user to create a minimal profile
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const minimalUser: IXVerifyUser = {
            id: authUser.id,
            email: authUser.email || '',
            full_name: authUser.user_metadata?.full_name || null,
            avatar_url: null,
            site_id: siteId,
            role: null,
            ixverify_role: null,
            failed_login_attempts: 0,
            locked_until: null,
            last_login_at: null,
            created_at: authUser.created_at,
            updated_at: authUser.created_at,
          };
          setUser(minimalUser);
        } else {
          setUser(null);
        }
      } else if (data.deleted_at) {
        // User has been soft deleted - sign them out
        console.log('[IXVerifyAuthContext] User account has been deleted');
        await supabase.auth.signOut();
        setUser(null);
      } else {
        // Fetch IXVerify role from tool_user_roles table via API
        let ixverifyRole: IXVerifyRole | null = null;
        if (siteId) {
          try {
            const roleResponse = await api.get(`/api/my-tool-role/ixverify?site_id=${siteId}`);
            ixverifyRole = roleResponse.data?.role as IXVerifyRole || null;
          } catch (roleError) {
            console.warn('[IXVerifyAuthContext] Could not fetch IXVerify role:', roleError);
          }
        }

        const ixVerifyUser: IXVerifyUser = {
          id: data.id,
          email: data.email,
          full_name: data.full_name,
          avatar_url: null,
          site_id: siteId,
          role: ixverifyRole, // Alias for compatibility
          ixverify_role: ixverifyRole,
          failed_login_attempts: data.failed_login_attempts || 0,
          locked_until: data.locked_until,
          last_login_at: data.last_login_at,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };
        setUser(ixVerifyUser);

        // Log successful login (get auth method from current session)
        const currentSession = (await supabase.auth.getSession()).data.session;
        const authMethodForLog = currentSession ? getAuthMethodFromSession(currentSession) : 'password';
        await logLoginSuccess(ixVerifyUser.id, ixVerifyUser.site_id || null, authMethodForLog || 'password');
      }
    } catch (error) {
      console.error('[IXVerifyAuthContext] Exception fetching user profile:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    if (error) {
      setLoading(false);
      throw error;
    }

    // If email confirmation is disabled, create the user profile immediately
    if (data.user && !data.user.identities?.length) {
      // User already exists
      setLoading(false);
      throw new Error('An account with this email already exists');
    }

    setLoading(false);
  };

  const signInWithOAuth = async (provider: 'google' | 'azure') => {
    setLoading(true);

    // Store invitation token if present in URL before OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const invitationToken = urlParams.get('token');
    if (invitationToken) {
      localStorage.setItem(INVITATION_TOKEN_KEY, invitationToken);
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/ixverify/dashboard`,
      },
    });

    if (error) {
      // Clear stored token on error
      localStorage.removeItem(INVITATION_TOKEN_KEY);
      setLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    // Log the logout before clearing state
    if (userRef.current) {
      await logLogout(userRef.current.id, null, 'manual');
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setSession(null);
    setSupabaseUser(null);
    setAuthMethod(null);
  };

  const dismissWarning = useCallback(() => {
    setWarningDismissed(true);
  }, []);

  return (
    <IXVerifyAuthContext.Provider
      value={{
        user,
        session,
        supabaseUser,
        loading,
        authMethod,
        sessionTimeout: {
          isWarningVisible: isWarningVisible && !warningDismissed,
          minutesRemaining,
          dismissWarning,
        },
        signIn,
        signUp,
        signInWithOAuth,
        signOut,
      }}
    >
      {children}
    </IXVerifyAuthContext.Provider>
  );
}

export function useIXVerifyAuth() {
  const context = useContext(IXVerifyAuthContext);
  if (context === undefined) {
    throw new Error('useIXVerifyAuth must be used within an IXVerifyAuthProvider');
  }
  return context;
}
